import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { FileText, Download, Users, BarChart3 } from 'lucide-react';

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = ['admin','toplevman','manager','marketing'].includes(user?.role);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [period, setPeriod] = useState('monthly');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (isAdmin) api.get('/api/agents').then(r => setAgents(r.data.agents)).catch(() => {});
  }, [isAdmin]);

  const loadReport = async () => {
    if (!selectedAgent) return;
    setLoading(true);
    try {
      const now = new Date();
      const res = await api.get(`/api/reports/executive/${selectedAgent}?period=${period}&month=${now.getMonth()+1}&year=${now.getFullYear()}`);
      setReport(res.data);
    } catch (err) { alert('Error loading report'); }
    finally { setLoading(false); }
  };

  const exportAll = async () => {
    setExporting(true);
    try {
      const res = await api.get('/api/export/full', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `kapsons_crm_export.zip`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { alert('Error exporting data'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div><h3 className="text-xl font-bold text-white">Reports</h3><p className="text-dark-400 text-sm">Executive reports and data export</p></div>
        {isAdmin && <button onClick={exportAll} disabled={exporting} className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg text-sm disabled:opacity-50"><Download className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export All Data (CSV)'}</button>}
      </div>

      <div className="glass rounded-xl p-5">
        <h4 className="text-white font-medium mb-3">Executive Report</h4>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <select value={selectedAgent} onChange={e => setSelectedAgent(e.target.value)} className="flex-1 px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
            <option value="">Select Executive</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.role})</option>)}
          </select>
          <select value={period} onChange={e => setPeriod(e.target.value)} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
            <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="half_yearly">Half-Yearly</option><option value="yearly">Yearly</option>
          </select>
          <button onClick={loadReport} disabled={!selectedAgent || loading} className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm disabled:opacity-50">{loading ? 'Loading...' : 'Generate'}</button>
          {report && <button onClick={() => {
            const rows = [
              ['Field', 'Value'],
              ['Agent', report.agent?.name],
              ['Role', report.agent?.role],
              ['Period', `${report.period?.start} to ${report.period?.end}`],
              ['Meetings Completed', report.meetings?.completed],
              ['Meetings Total', report.meetings?.total],
              ['Deals Closed', report.deals?.closed],
              ['Commission (AED)', report.commission],
              ['Total Calls', report.calls?.total],
              ['VoIP Calls', report.calls?.voip],
              ['Attendance Days', report.attendance_days],
              ['Active Minutes', report.active_minutes],
              ['Salary Total (INR)', report.salary_total],
            ];
            const csv = rows.map(r => r.join(',')).join('\n');
            const a = document.createElement('a');
            a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
            a.download = `report_${report.agent?.name}_${report.period?.type}.csv`;
            a.click();
          }} className="px-4 py-2 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded-lg text-sm flex items-center gap-1"><Download className="w-4 h-4"/>Export CSV</button>}
        </div>

        {report && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-dark-800/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center"><span className="text-primary-400 font-bold">{report.agent?.name?.charAt(0)}</span></div>
              <div>
                <p className="text-white font-medium">{report.agent?.name}</p>
                <p className="text-dark-400 text-xs">{report.agent?.role} | {report.period?.type} ({report.period?.start} to {report.period?.end})</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <ReportStat label="Meetings" value={`${report.meetings?.completed}/${report.meetings?.total}`} />
              <ReportStat label="Deals Closed" value={report.deals?.closed} />
              <ReportStat label="Commission (AED)" value={`AED ${report.commission?.toLocaleString()}`} />
              <ReportStat label="Total Calls" value={report.calls?.total} />
              <ReportStat label="Attendance" value={`${report.attendance_days} days`} />
              <ReportStat label="Active Time" value={`${report.active_minutes} min`} />
              <ReportStat label="Salary (INR)" value={`₹${report.salary_total?.toLocaleString()}`} />
              <ReportStat label="VoIP Calls" value={report.calls?.voip} />
            </div>

            {report.payrolls?.length > 0 && (
              <div>
                <h5 className="text-white font-medium mb-2">Payroll History</h5>
                <div className="space-y-2">
                  {report.payrolls.map(p => (
                    <div key={p.id} className="flex justify-between items-center p-3 bg-dark-800/30 rounded-lg text-sm">
                      <span className="text-white">{p.period}</span>
                      <span className="text-dark-400">{p.completed_meetings} meetings</span>
                      <span className="text-green-400 font-medium">₹{p.total_pay?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ReportStat({ label, value }) {
  return (
    <div className="glass rounded-lg p-3 text-center">
      <div className="text-lg font-bold text-white">{value}</div>
      <div className="text-xs text-dark-400">{label}</div>
    </div>
  );
}
